import jsPDF from 'jspdf';

/**
 * Genera la Ficha Técnica de Identificación del Distribuidor en formato PDF.
 * Diseño corporativo ejecutivo de alto nivel (Slate & Sapphire Corporate).
 * @param {Object} params
 * @param {Object} params.distribuidor - Información del cliente / distribuidor
 * @param {Object} params.estadisticas - Estadísticas de órdenes de spr_AdminPanel_Distribuidor_GetEstadisticasOrdenes
 */
export const exportFichaDistribuidorPdf = ({ distribuidor = {}, estadisticas = {} }) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
    });

    const pageWidth = 215.9;   // Letter width
    const pageHeight = 279.4;  // Letter height
    const margin = 14;
    const contentWidth = pageWidth - (margin * 2);

    // ==========================================
    // PALETA CORPORATIVA AKASIA BLUE
    // ==========================================
    const colorPrimaryBlue = [2, 132, 199];   // #0284c7 (Akasia Corporate Blue)
    const colorDarkBlue = [3, 105, 161];      // #0369a1 (Deep Accent Blue)
    const colorDark = [30, 41, 59];           // #1e293b (Slate-800 - Texto principal)
    const colorMuted = [100, 116, 139];       // #64748b (Slate-500 - Etiquetas y secundarios)
    const colorLightBg = [248, 250, 252];     // #f8fafc (Fondo tarjetas)
    const colorBorder = [226, 232, 240];      // #e2e8f0 (Bordes)
    const colorBadgeBg = [224, 242, 254];     // #e0f2fe (Sky-100)
    const colorBorderBlue = [186, 230, 253];  // #bae6fd (Sky-200)

    // ==========================================
    // 1. ENCABEZADO INSTITUCIONAL
    // ==========================================
    // Barra superior sólida en Azul Corporativo Akasia
    doc.setFillColor(colorPrimaryBlue[0], colorPrimaryBlue[1], colorPrimaryBlue[2]);
    doc.rect(0, 0, pageWidth, 24, 'F');

    // Línea de acento inferior en Deep Blue
    doc.setFillColor(colorDarkBlue[0], colorDarkBlue[1], colorDarkBlue[2]);
    doc.rect(0, 24, pageWidth, 1.8, 'F');

    // Título Principal de la Empresa
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('AKASIA SOFTWARE DEVELOPMENT S.A. DE C.V.', margin, 11);

    // Subtítulo institucional
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(224, 242, 254);
    doc.text('FICHA TÉCNICA DE IDENTIFICACIÓN DE DISTRIBUIDOR', margin, 17);

    // Metadatos de emisión (derecha)
    const hoy = new Date();
    const fechaEmision = hoy.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
    const idCliente = distribuidor.idClienteAkasia || 'N/A';

    doc.setFontSize(8);
    doc.setTextColor(224, 242, 254);
    doc.text(`Fecha de Emisión: ${fechaEmision}`, pageWidth - margin, 11, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`Folio Oficial: #${idCliente}`, pageWidth - margin, 17, { align: 'right' });

    let y = 32;

    // ==========================================
    // 2. TARJETA DE IDENTIDAD DEL DISTRIBUIDOR
    // ==========================================
    doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
    doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
    doc.roundedRect(margin, y, contentWidth, 31, 2, 2, 'FD');

    // Barra vertical decorativa izquierda (Azul Akasia)
    doc.setFillColor(colorPrimaryBlue[0], colorPrimaryBlue[1], colorPrimaryBlue[2]);
    doc.roundedRect(margin, y, 2.5, 31, 1, 1, 'F');

    // Badges en la cabecera del distribuidor
    doc.setFillColor(colorBadgeBg[0], colorBadgeBg[1], colorBadgeBg[2]);
    doc.roundedRect(margin + 6, y + 4.5, 28, 5, 1, 1, 'F');
    doc.setTextColor(colorDarkBlue[0], colorDarkBlue[1], colorDarkBlue[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(`ID: #${idCliente}`, margin + 20, y + 8, { align: 'center' });

    // Badge de tipo y estatus
    const esActivo = distribuidor.bIsActive !== false;
    doc.setFillColor(esActivo ? 236 : 254, esActivo ? 253 : 242, esActivo ? 245 : 242);
    doc.roundedRect(margin + 36, y + 4.5, 38, 5, 1, 1, 'F');
    doc.setTextColor(esActivo ? 4 : 185, esActivo ? 120 : 28, esActivo ? 87 : 28);
    doc.text(esActivo ? 'DISTRIBUIDOR • ACTIVO' : 'DISTRIBUIDOR • INACTIVO', margin + 55, y + 8, { align: 'center' });

    // Nombre Completo del Distribuidor
    const nombreCompleto = distribuidor.vNombreDistribuidor ||
        `${distribuidor.vNombre || ''} ${distribuidor.vApellidos || ''}`.trim() || 'Sin Nombre';

    doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    let nombreTexto = nombreCompleto.toUpperCase();
    while (doc.getTextWidth(nombreTexto) > (contentWidth - 14) && nombreTexto.length > 3) {
        nombreTexto = nombreTexto.slice(0, -4) + '...';
    }
    doc.text(nombreTexto, margin + 6, y + 16.5);

    // Metadatos inferiores de la identidad
    const midX = margin + (contentWidth / 2) + 2;
    const maxLeftWidth = (contentWidth / 2) - 34;
    const maxRightWidth = (contentWidth / 2) - 28;

    // --- Columna Izquierda (Identidad) ---
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
    doc.text('Razón Social:', margin + 6, y + 22.5);
    doc.text('Agente Asignado:', margin + 6, y + 27.5);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);

    let rSocial = String(distribuidor.vRazonSocial || 'No especificada');
    while (doc.getTextWidth(rSocial) > maxLeftWidth && rSocial.length > 3) {
        rSocial = rSocial.slice(0, -4) + '...';
    }
    doc.text(rSocial, margin + 27, y + 22.5);

    let agenteStr = String(distribuidor.vAgente || distribuidor.vCreatedUser || 'Sin agente');
    while (doc.getTextWidth(agenteStr) > (maxLeftWidth - 5) && agenteStr.length > 3) {
        agenteStr = agenteStr.slice(0, -4) + '...';
    }
    doc.text(agenteStr, margin + 32, y + 27.5);

    // --- Columna Derecha (Identidad) ---
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
    doc.text('Fecha Registro:', midX, y + 22.5);
    doc.text('Ubicación:', midX, y + 27.5);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);

    let fAlta = String(distribuidor.reg_desde || distribuidor.joinDate || 'N/A');
    while (doc.getTextWidth(fAlta) > maxRightWidth && fAlta.length > 3) {
        fAlta = fAlta.slice(0, -4) + '...';
    }
    doc.text(fAlta, midX + 24, y + 22.5);

    let ubi = String([distribuidor.vEstadoDepProv, distribuidor.vPais].filter(Boolean).join(', ') || 'N/A');
    while (doc.getTextWidth(ubi) > maxRightWidth && ubi.length > 3) {
        ubi = ubi.slice(0, -4) + '...';
    }
    doc.text(ubi, midX + 17, y + 27.5);

    y += 36;

    // ==========================================
    // 3. SECCIÓN 2 COLUMNAS: CONTACTO & FISCAL
    // ==========================================
    const colWidth = (contentWidth - 6) / 2;

    const sanitizeVal = (val) => {
        if (val === undefined || val === null || val === '') return 'N/A';
        const str = String(val).replace(/\r?\n+/g, ', ').replace(/\s{2,}/g, ' ').trim();
        return str || 'N/A';
    };

    const prepareRows = (rawRows, availableWidth) => {
        return rawRows.map(row => {
            const label = row.label;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.6);
            const labelW = doc.getTextWidth(label);
            const maxValW = availableWidth - labelW - 6;

            const cleanVal = sanitizeVal(row.value);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);

            let lines = doc.splitTextToSize(cleanVal, maxValW);
            if (lines.length > 2) {
                let secondLine = lines[1];
                while (doc.getTextWidth(secondLine + '...') > maxValW && secondLine.length > 3) {
                    secondLine = secondLine.slice(0, -1);
                }
                lines = [lines[0], secondLine + '...'];
            }
            const rowH = lines.length > 1 ? 9.2 : 5.8;
            return { label, lines, rowH };
        });
    };

    const contactRows = prepareRows([
        { label: 'Correo Electrónico', value: distribuidor.vEmail },
        { label: 'Teléfono / WhatsApp', value: distribuidor.vTelefonoPersonal || distribuidor.vTelefonoTrabajo },
        { label: 'País', value: distribuidor.vPais },
        { label: 'Estado / Depto.', value: distribuidor.vEstadoDepProv },
        { label: 'Código Postal', value: distribuidor.vCp },

    ], colWidth - 8);

    const fiscalRows = prepareRows([
        { label: 'RFC / Identificación', value: distribuidor.vRfc },
        { label: 'Núm. Reg. Tributario', value: distribuidor.vNumRegTrib },
        { label: 'Régimen Fiscal', value: distribuidor.vDescripcionRegimenFiscal || distribuidor.vCodigoRegimen },
        { label: 'Uso CFDI', value: distribuidor.vDescripcionUsoCFDI || distribuidor.vCodigoUso },
        { label: 'País Facturación', value: distribuidor.vPaisFacturacion },
        { label: 'Dirección Fiscal', value: distribuidor.vDireccionFiscal }
    ], colWidth - 8);

    const contactContentH = contactRows.reduce((sum, r) => sum + r.rowH, 0);
    const fiscalContentH = fiscalRows.reduce((sum, r) => sum + r.rowH, 0);
    const cardHeaderH = 7;
    const cardPaddingTop = 4;
    const cardPaddingBottom = 3;
    const sectionHeight = cardHeaderH + cardPaddingTop + Math.max(contactContentH, fiscalContentH) + cardPaddingBottom;

    const renderCard = (title, rows, startX) => {
        // Fondo y borde
        doc.setFillColor(colorLightBg[0], colorLightBg[1], colorLightBg[2]);
        doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
        doc.roundedRect(startX, y, colWidth, sectionHeight, 2, 2, 'FD');

        // Header azul corporativo Akasia
        doc.setFillColor(colorPrimaryBlue[0], colorPrimaryBlue[1], colorPrimaryBlue[2]);
        doc.roundedRect(startX, y, colWidth, cardHeaderH, 2, 2, 'F');
        doc.rect(startX, y + cardHeaderH - 2, colWidth, 2, 'F');

        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(title, startX + 4, y + 4.8);

        // Filas de datos
        let currentY = y + cardHeaderH + cardPaddingTop + 1.5;
        rows.forEach((row, idx) => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7.6);
            doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
            doc.text(row.label, startX + 4, currentY);

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(colorDark[0], colorDark[1], colorDark[2]);

            if (row.lines.length === 1) {
                doc.text(row.lines[0], startX + colWidth - 4, currentY, { align: 'right' });
            } else {
                row.lines.forEach((line, lIdx) => {
                    doc.text(line, startX + colWidth - 4, currentY + (lIdx * 3.6), { align: 'right' });
                });
            }

            // Divisor sutil debajo de la fila (excepto la última)
            if (idx < rows.length - 1) {
                const dividerY = currentY + (row.lines.length > 1 ? 5.2 : 2.2);
                doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
                doc.line(startX + 4, dividerY, startX + colWidth - 4, dividerY);
            }

            currentY += row.rowH;
        });
    };

    renderCard('INFORMACIÓN DE CONTACTO', contactRows, margin);
    const col2X = margin + colWidth + 6;
    renderCard('DATOS FISCALES Y FACTURACIÓN', fiscalRows, col2X);

    y += sectionHeight + 7;

    // ==========================================
    // 4. PIE DE PÁGINA INSTITUCIONAL
    // ==========================================
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setDrawColor(colorBorder[0], colorBorder[1], colorBorder[2]);
        doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.setTextColor(colorMuted[0], colorMuted[1], colorMuted[2]);
        doc.text('AKASIA SOFTWARE DEVELOPMENT S.A. DE C.V. © - Todos los derechos reservados.', margin, pageHeight - 8);
        doc.text(`Distribuidor #${idCliente}  |  Pág. ${i} de ${totalPages}`, pageWidth - margin, pageHeight - 8, { align: 'right' });
    }

    // Guardar / Descargar PDF
    const filename = `Ficha_Distribuidor_${idCliente}.pdf`;
    doc.save(filename);
};
